import React, { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../../lib/api";
import { T, T_EMISORES, rutaEmisor, REGIMENES_EMISOR, IVAS, datosDelError, MANIFIESTO_URL, useRetornoPaqueteCfdi } from "../../lib/cfdi";
import { useAuth } from "../../contexts/AuthContext";
import HistorialCfdis from "./HistorialCfdis";
import FacturaGlobal from "./FacturaGlobal";
import PaqueteCfdi from "./PaqueteCfdi";

// Pestaña «Facturas a pacientes» de Facturación (CL.4 C): emisor, CSD, modo, cupo, datos SAT de
// los servicios y el historial de facturas (CL.4 C3, HistorialCfdis). Sólo se monta si el
// backend la ofrece (bandera).
// 🔴 El CSD (.cer, .key, contraseña) se manda en memoria al backend, que lo reenvía al servicio
//    de facturación; no se guarda en el navegador ni se vuelve a mostrar.
// Textos: lib/cfdi.js — # Copy aprobado por Jesús (4 Oct 2026; Manifiesto 15 Oct 2026).
const campo = "mt-1 h-9 w-full rounded-lg border border-slate-200 bg-white px-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-electric";
const tarjeta = "rounded-2xl border border-slate-200 bg-white p-5";
const btnPrim = "rounded-lg bg-brand-electric px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50";
const btnSec = "rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50";
const PASOS = { manifiesto: T.pasoManifiesto };

function fechaCorta(iso) {
  if (!iso) return "—";
  const [y, m, d] = String(iso).slice(0, 10).split("-");
  return `${d}/${m}/${y}`;
}

export default function EmisorCfdiPanel({ estado, error, onCambio }) {
  const { puede } = useAuth();
  const configuracion = puede("configuracion");
  const [lista, setLista] = useState(null);
  const [doctorId, setDoctorId] = useState("");
  const [altaId, setAltaId] = useState("");
  const [seleccion, setSeleccion] = useState(null);
  const [errorSeleccion, setErrorSeleccion] = useState(null);
  const estadoMedico = seleccion?.doctorId === doctorId ? seleccion.data : null;
  const seleccionActual = useRef(doctorId);
  seleccionActual.current = doctorId;

  const cargarEmisores = useCallback(async () => {
    try {
      const { data } = await api.get("/cfdi-pacientes/emisores");
      setLista(data || null);
    } catch (e) {
      // El backend anterior al DDL conserva el panel de clínica.
      // Si falla un refresco, se conserva el selector ya cargado para volver a clínica.
    }
  }, []);
  useEffect(() => { if (estado && configuracion) cargarEmisores(); }, [estado, configuracion, cargarEmisores]);
  useEffect(() => {
    let vigente = true;
    setSeleccion(null);
    setErrorSeleccion(null);
    if (doctorId) {
      api.get(rutaEmisor("/estado", doctorId)).then(
        ({ data }) => { if (vigente) setSeleccion({ doctorId, data }); },
        (e) => { if (vigente) setErrorSeleccion(datosDelError(e).detail); }
      );
    }
    return () => { vigente = false; };
  }, [doctorId]);

  const recargarMedico = async ({ exigirFacturapi = false } = {}) => {
    const { data } = await api.get(rutaEmisor("/estado", doctorId));
    if (exigirFacturapi && !Array.isArray(data?.facturapi?.pending_steps)) throw new Error();
    if (seleccionActual.current === doctorId) setSeleccion({ doctorId, data });
    await cargarEmisores();
    return { estado: data };
  };
  const doctores = lista?.doctores || [];
  const emisores = lista?.emisores || [];
  const conEmisor = new Set(emisores.map((e) => e.doctor_id).filter(Boolean));
  const etiqueta = (id) => T_EMISORES.medico(doctores.find((d) => d.id === id)?.name || id);
  return (
    <div className="space-y-4">
      {estado && configuracion && lista && (
        <div className={tarjeta}>
          <label className="text-sm">{T_EMISORES.emisor}
            <select className={campo} value={doctorId} disabled={!estadoMedico && !!doctorId && !errorSeleccion}
                    onChange={(e) => setDoctorId(e.target.value)} data-testid="emisor-selector">
              <option value="">{T_EMISORES.clinica}</option>
              {[...new Set([...conEmisor, ...(doctorId ? [doctorId] : [])])].map((id) => (
                <option key={id} value={id}>{etiqueta(id)}</option>
              ))}
            </select>
          </label>
          <div className="mt-3 flex gap-2">
            <select className={campo} value={altaId} onChange={(e) => setAltaId(e.target.value)}
                    aria-label={T_EMISORES.elegir} data-testid="emisor-alta-medico">
              <option value="">{T_EMISORES.elegir}</option>
              {doctores.filter((d) => !conEmisor.has(d.id)).map((d) => (
                <option key={d.id} value={d.id}>{T_EMISORES.medico(d.name)}</option>
              ))}
            </select>
            <button type="button" className={btnSec} disabled={!altaId || (!!doctorId && !estadoMedico && !errorSeleccion)}
                    onClick={() => { setDoctorId(altaId); setAltaId(""); }} data-testid="emisor-alta">
              {T_EMISORES.alta}
            </button>
          </div>
        </div>
      )}
      {errorSeleccion && <p role="alert" className="text-sm text-red-600">{errorSeleccion}</p>}
      {(doctorId ? estadoMedico : estado)?.global_disponible && puede("cfdi") && (doctorId ? estadoMedico : estado)?.emisor?.id && (
        <FacturaGlobal key={`global:${doctorId}:${(doctorId ? estadoMedico : estado).emisor.id}`} emisorId={(doctorId ? estadoMedico : estado).emisor.id}
                       ultimoMes={(doctorId ? estadoMedico : estado).ultimo_mes_cerrado}
                       onCambio={doctorId ? recargarMedico : onCambio} />
      )}
      {(!doctorId || estadoMedico) && (
        <EmisorContenido key={doctorId} doctorId={doctorId} estado={doctorId ? estadoMedico : estado}
                         error={error} onCambio={doctorId ? recargarMedico : onCambio} />
      )}
      {estado && (estado.emisor || lista?.emisores?.length > 0) && <HistorialCfdis />}
    </div>
  );
}

function EmisorContenido({ estado, error, onCambio, doctorId }) {
  const { puede } = useAuth();
  const avisoPaquete = useRetornoPaqueteCfdi(onCambio);
  const emisor = estado?.emisor || null;
  const [f, setF] = useState({ razon_social: "", regimen: "612", cp: "", clave_sat_omision: "", iva_omision: "exento" });
  const [msg, setMsg] = useState({});           // {seccion: {ok|error}}
  const [ocupado, setOcupado] = useState(null);
  const [csd, setCsd] = useState({ cer: null, key: null, password: "" });
  const [servicios, setServicios] = useState(null);
  const [firmaAbierta, setFirmaAbierta] = useState(false);
  const [revisandoFirma, setRevisandoFirma] = useState(false);

  useEffect(() => {
    if (emisor) {
      setF({ razon_social: emisor.razon_social || "", regimen: emisor.regimen || "612", cp: emisor.cp || "",
             clave_sat_omision: emisor.clave_sat_omision || "", iva_omision: emisor.iva_omision || "exento" });
    }
  }, [emisor]);

  const cargarListas = useCallback(async () => {
    try {
      const s = await api.get("/cfdi-pacientes/servicios");
      setServicios(s.data?.servicios || []);
    } catch (e) {
      setServicios([]);
      setMsg((m) => ({ ...m, listas: { error: datosDelError(e).detail } }));
    }
  }, []);

  useEffect(() => { if (estado) cargarListas(); }, [estado, cargarListas]);

  if (error && !estado) {
    return (
      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6 text-sm text-amber-800" data-testid="cfdi-panel-error">
        {error}
      </div>
    );
  }
  if (!estado) return null;

  const hacer = async (seccion, fn) => {
    setOcupado(seccion);
    setMsg((m) => ({ ...m, [seccion]: null }));
    try {
      await fn();
      setMsg((m) => ({ ...m, [seccion]: { ok: true } }));
      await onCambio?.();
    } catch (e) {
      setMsg((m) => ({ ...m, [seccion]: { error: datosDelError(e).detail } }));
    } finally {
      setOcupado(null);
    }
  };

  const guardarEmisor = () => hacer("emisor", () => api.put(rutaEmisor("", doctorId), {
    razon_social: f.razon_social.trim(), regimen: f.regimen, cp: f.cp.trim(),
    clave_sat_omision: f.clave_sat_omision.trim(), iva_omision: f.iva_omision,
  }));

  const subirCsd = () => hacer("csd", async () => {
    const fd = new FormData();
    fd.append("cer", csd.cer);
    fd.append("key", csd.key);
    fd.append("password", csd.password);
    await api.post(rutaEmisor("/csd", doctorId), fd);
    setCsd({ cer: null, key: null, password: "" });
  });

  const activarLive = () => hacer("live", () => api.post(rutaEmisor("/live", doctorId)));

  const revisarManifiesto = async () => {
    setRevisandoFirma(true);
    setMsg((m) => ({ ...m, manifiesto: null }));
    try {
      // onCambio = useEstadoCfdi.recargar: invalida la caché y hace un GET nuevo.
      const res = await onCambio({ exigirFacturapi: true });
      const pendientes = res?.estado?.facturapi?.pending_steps;
      // El backend puede devolver facturapi: null si el proveedor no responde.
      // Esa ausencia no confirma una firma; reutiliza el error del panel.
      if (!Array.isArray(pendientes)) {
        throw new Error();
      }
      const pendiente = pendientes.includes("manifiesto");
      setMsg((m) => ({ ...m, manifiesto: { texto: pendiente ? T.manifiestoPendiente : T.manifiestoListo } }));
      if (!pendiente) setFirmaAbierta(false);
    } catch (e) {
      setMsg((m) => ({ ...m, manifiesto: { error: datosDelError(e).detail } }));
    } finally {
      setRevisandoFirma(false);
    }
  };

  const guardarServicio = (s) => hacer(`srv-${s.id}`, async () => {
    const { data } = await api.put(`/cfdi-pacientes/servicios/${s.id}`,
      { clave_sat: (s.clave_sat || "").trim(), clave_unidad_sat: (s.clave_unidad_sat || "E48").trim().toUpperCase(), iva: s.iva });
    setServicios((xs) => xs.map((x) => (x.id === s.id ? data : x)));
  });

  const nota = (seccion) => {
    const m = msg[seccion];
    if (!m) return null;
    return m.error
      ? <p className="mt-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600" data-testid={`cfdi-msg-${seccion}`}>{m.error}</p>
      : <p className="mt-2 text-sm text-emerald-700" data-testid={`cfdi-msg-${seccion}`}>{T.guardado}</p>;
  };

  const pasos = estado.facturapi?.pending_steps || [];
  const cupo = estado.cupo || {};

  return (
    <div className="space-y-4" data-testid="cfdi-panel">
      <div className={`rounded-[10px] border px-4 py-3 text-sm ${estado.modo === "live"
        ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-amber-200 bg-amber-50 text-amber-800"}`}
           data-testid="cfdi-modo">
        {estado.modo === "live" ? T.modoReal : T.modoPrueba}
        <span className="ml-2 text-slate-600" data-testid="cfdi-cupo">
          · {cupo.maximo == null ? T.cupoNoDisponible : T.cupo(cupo.usados, cupo.maximo)}
        </span>
        <PaqueteCfdi cupo={cupo} puede={puede} />
      </div>
      {avisoPaquete && <p role="status" data-testid="cfdi-paquete-retorno" className="text-sm text-slate-600">{avisoPaquete}</p>}

      <div className={tarjeta}>
        <h3 className="font-semibold text-slate-900">{doctorId ? T_EMISORES.datos : T.emisorTitulo}</h3>
        <p className="mb-3 text-xs text-slate-500">{T.emisorSub}</p>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="text-xs font-medium text-slate-600 sm:col-span-2">{T.razonSocial}
            <input className={campo} value={f.razon_social} onChange={(e) => setF({ ...f, razon_social: e.target.value })} data-testid="emisor-razon" />
          </label>
          <label className="text-xs font-medium text-slate-600">{T.regimen}
            <select className={campo} value={f.regimen} onChange={(e) => setF({ ...f, regimen: e.target.value })} data-testid="emisor-regimen">
              {REGIMENES_EMISOR.map((r) => <option key={r.code} value={r.code}>{r.label}</option>)}
            </select>
          </label>
          <label className="text-xs font-medium text-slate-600">{T.cp}
            <input className={campo} maxLength={5} value={f.cp} onChange={(e) => setF({ ...f, cp: e.target.value })} data-testid="emisor-cp" />
          </label>
          <label className="text-xs font-medium text-slate-600">{T.claveOmision}
            <input className={campo} maxLength={8} value={f.clave_sat_omision} onChange={(e) => setF({ ...f, clave_sat_omision: e.target.value })} data-testid="emisor-clave" />
          </label>
          <label className="text-xs font-medium text-slate-600">{T.ivaOmision}
            <select className={campo} value={f.iva_omision} onChange={(e) => setF({ ...f, iva_omision: e.target.value })} data-testid="emisor-iva">
              {IVAS.map((i) => <option key={i.code} value={i.code}>{i.label}</option>)}
            </select>
          </label>
        </div>
        {nota("emisor")}
        <button type="button" className={`${btnPrim} mt-3`} disabled={ocupado === "emisor"} onClick={guardarEmisor} data-testid="emisor-guardar">
          {ocupado === "emisor" ? T.guardando : T.guardar}
        </button>
      </div>

      {emisor && (
        <div className={tarjeta}>
          <h3 className="font-semibold text-slate-900">{T.csdTitulo}</h3>
          <p className="mb-2 text-xs text-slate-500">{T.csdSub}</p>
          <p className="mb-3 text-sm text-slate-700" data-testid="csd-estado">
            {emisor.tiene_csd ? T.csdVigente(emisor.rfc, fechaCorta(emisor.csd_vence)) : T.csdSinCargar}
          </p>
          <div className="grid gap-3 sm:grid-cols-3">
            <label className="text-xs font-medium text-slate-600">{T.csdCer}
              <input type="file" accept=".cer" className="mt-1 block w-full text-xs" onChange={(e) => setCsd({ ...csd, cer: e.target.files?.[0] || null })} data-testid="csd-cer" />
            </label>
            <label className="text-xs font-medium text-slate-600">{T.csdKey}
              <input type="file" accept=".key" className="mt-1 block w-full text-xs" onChange={(e) => setCsd({ ...csd, key: e.target.files?.[0] || null })} data-testid="csd-key" />
            </label>
            <label className="text-xs font-medium text-slate-600">{T.csdPassword}
              <input type="password" autoComplete="off" className={campo} value={csd.password} onChange={(e) => setCsd({ ...csd, password: e.target.value })} data-testid="csd-password" />
            </label>
          </div>
          {nota("csd")}
          <button type="button" className={`${btnPrim} mt-3`} disabled={ocupado === "csd" || !csd.cer || !csd.key || !csd.password}
                  onClick={subirCsd} data-testid="csd-subir">
            {ocupado === "csd" ? T.guardando : T.csdSubir}
          </button>
        </div>
      )}

      {emisor && estado.modo_global === "live" && !emisor.live_habilitado && (
        <div className={tarjeta} data-testid="cfdi-live">
          {pasos.length > 0 && (
            <>
              <p className="text-sm text-slate-700">{T.pendientes}</p>
              <ul className="mb-3 list-disc pl-5 text-sm text-slate-700">
                {pasos.map((p) => <li key={p}>{PASOS[p] || p}</li>)}
              </ul>
            </>
          )}
          {nota("live")}
          <button type="button" className={btnPrim} disabled={ocupado === "live"} onClick={activarLive} data-testid="cfdi-activar-live">
            {T.activarLive}
          </button>
        </div>
      )}

      {puede("configuracion") && pasos.includes("manifiesto") && (
        <div className={tarjeta} data-testid="cfdi-manifiesto">
          <h3 className="font-semibold text-slate-900">{T.manifiestoTitulo}</h3>
          <p className="mt-2 text-sm text-slate-700">{T.manifiestoCuerpo(emisor?.rfc)}</p>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <button type="button" className={btnPrim} disabled={revisandoFirma}
                    onClick={() => setFirmaAbierta(true)} data-testid="cfdi-manifiesto-firmar">
              {T.manifiestoFirmar}
            </button>
            <a href={MANIFIESTO_URL} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-700 underline">
              {T.manifiestoAbrir}
            </a>
            <button type="button" className={btnSec} disabled={revisandoFirma}
                    onClick={revisarManifiesto} data-testid="cfdi-manifiesto-revisar">
              {T.manifiestoRevisar}
            </button>
          </div>
          {firmaAbierta && (
            <iframe src={MANIFIESTO_URL} title={T.manifiestoIframe} className="mt-4 w-full border-0"
                    height="720" data-testid="cfdi-manifiesto-iframe" />
          )}
        </div>
      )}
      {puede("configuracion") && msg.manifiesto && (
        msg.manifiesto.error ? nota("manifiesto") : (
          <p role="status" className="text-sm text-emerald-700" data-testid="cfdi-msg-manifiesto">{msg.manifiesto.texto}</p>
        )
      )}

      {emisor && servicios && servicios.length > 0 && (
        <div className={tarjeta} data-testid="cfdi-servicios">
          <h3 className="font-semibold text-slate-900">{T.serviciosTitulo}</h3>
          <p className="mb-3 text-xs text-slate-500">{T.serviciosSub}</p>
          <div className="space-y-2">
            {servicios.map((s) => (
              <div key={s.id} className="grid grid-cols-2 items-end gap-2 sm:grid-cols-[1fr_7rem_5rem_7rem_auto]">
                <div className="col-span-2 text-sm text-slate-800 sm:col-span-1">{s.name}</div>
                <label className="text-[11px] text-slate-500">{T.claveSat}
                  <input className={campo} maxLength={8} value={s.clave_sat || ""} data-testid={`srv-clave-${s.id}`}
                         onChange={(e) => setServicios((xs) => xs.map((x) => (x.id === s.id ? { ...x, clave_sat: e.target.value } : x)))} />
                </label>
                <label className="text-[11px] text-slate-500">{T.unidad}
                  <input className={campo} maxLength={3} value={s.clave_unidad_sat || ""}
                         onChange={(e) => setServicios((xs) => xs.map((x) => (x.id === s.id ? { ...x, clave_unidad_sat: e.target.value } : x)))} />
                </label>
                <label className="text-[11px] text-slate-500">{T.iva}
                  <select className={campo} value={s.iva} data-testid={`srv-iva-${s.id}`}
                          onChange={(e) => setServicios((xs) => xs.map((x) => (x.id === s.id ? { ...x, iva: e.target.value } : x)))}>
                    {IVAS.map((i) => <option key={i.code} value={i.code}>{i.label}</option>)}
                  </select>
                </label>
                <button type="button" className={btnSec} disabled={ocupado === `srv-${s.id}`} onClick={() => guardarServicio(s)} data-testid={`srv-guardar-${s.id}`}>
                  {T.guardar}
                </button>
                {msg[`srv-${s.id}`]?.error && <p className="col-span-full text-xs text-red-600">{msg[`srv-${s.id}`].error}</p>}
              </div>
            ))}
          </div>
        </div>
      )}

      {msg.listas?.error && <p className="text-xs text-red-600" data-testid="cfdi-msg-listas">{msg.listas.error}</p>}

    </div>
  );
}
